import type { Cart, CartResponse } from "~/types/Data/Checkout/Cart";
import type { VerticalCardProps } from "~/types/schemas/Product/Molecule/verticalCard";
import type { BaseResponse } from "~/types/Data/Common/APIs";
import { ProductTypes } from "~/constants/ProductManagement/productTypes";
import useTrackEvents from "~/composables/Common/useTrackEvents";
import paths from "~/constants/paths";

export default function useCart() {
  // TODO: can we combine both states cart and cart cookie
  const cart = useState<Cart | null>("cart", () => null);
  const savedCarts = useState<Cart[] | null>("saved-carts", () => null);
  const loadingProductId = useState("cart-product-id", () => "");

  const cartCookie = useCookie("hs-cart");
  const userToken = useCookie("hs-token");
  const nuxtApp = useNuxtApp();
  const router = useRouter();
  const { addToast } = useToast();
  const { trackAddToCart } = useTrackEvents();

  const isCartEmpty = computed(
    () => (cart.value && !cart.value.total_quantity) || !cartCookie.value,
  );

  const isInCart = (id: string) => {
    return !!(
      cart.value?.products?.some((item) => item.id === id) ||
      cart.value?.variants?.some((item) => item.id === id)
    );
  };

  const getItemQuantity = (type: ProductTypes, id: string) => {
    if (!cart.value) return 0;

    if (type === ProductTypes.PRODUCT) {
      const prod = cart.value.products?.find((p) => p.id === id);
      return prod?.quantity ?? 0;
    }

    if (type === ProductTypes.VARIANT) {
      const variant = cart.value.variants?.find((v) => v.id === id);
      return variant?.quantity ?? 0;
    }
    return 0;
  };

  const initCart = async (baseUrl: string, removeId?: boolean) => {
    if (cartCookie.value || userToken.value) {
      const { res, err } = await useApi<CartResponse>(
        `/api/v1/my-cart`,
        {
          authToken: userToken.value,
          query: {
            cart_id: removeId ? undefined : cartCookie.value,
          },
        },
        {
          baseUrl,
          catchError: true,
        },
      );

      //FIXME: backend should replace [] with null
      if (res?.data && !Array.isArray(res.data)) cart.value = res.data;

      if (res?.data && !cartCookie.value) {
        cartCookie.value = res.data.id;
      }

      if (err) {
        addToast("Failed to init cart", "error");
        resetCart();
      }
    }
  };

  const updateQuantity = async (
    type: ProductTypes,
    quantity: number,
    id: string,
    cart_id?: string,
  ) => {
    loadingProductId.value = id;

    if (!cart.value && !cart_id) {
      throw new Error(
        "Failed to update cart due to missing card_id and empty cart state",
      );
    }

    const { res } = await useApi<CartResponse>(`/api/v1/my-cart`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      authToken: userToken.value,
      body: {
        [type]: id,
        cart_id: cart_id ?? cart.value?.id,
        quantity,
      },
    });

    if (res?.data && !cart_id) cart.value = res.data;

    loadingProductId.value = "";
    return res?.data;
  };

  const addToCart = async (
    type: ProductTypes,
    id: string,
    quantity: number,
    price: string,
    others?: any,
  ) => {
    loadingProductId.value = id;
    const { res, err } = await useApi<CartResponse>(
      `/api/v1/add-to-cart`,
      {
        method: "POST",
        authToken: userToken.value,
        body: {
          [type]: id,
          cart_id: cart.value?.id,
          quantity,
          ...others,
        },
      },
      {
        catchError: true,
      },
    );

    if (err) {
      addToast("Failed to add to cart", "error");
    } else {
      cart.value = res?.data ?? null;
      cartCookie.value = res?.data.id;
      addToast("Added to cart", "success");
      trackAddToCart(id, type, price, nuxtApp.$currency);
    }
    loadingProductId.value = "";
  };

  const quickAddToCart = async (product: VerticalCardProps) => {
    if (
      product.has_variant &&
      product.default_variant_id &&
      product.variants_count === 1 &&
      product.price?.price
    ) {
      addToCart(
        ProductTypes.VARIANT,
        product.default_variant_id,
        1,
        product.price.price.toString(),
      );
    } else if (!product.has_variant && product.price?.price) {
      addToCart(
        ProductTypes.PRODUCT,
        product.id,
        1,
        product.price.price.toString(),
      );
    } else {
      router.push(`${paths.products}/${product.id}`);
    }
  };

  const saveCart = async (name: string) => {
    const { err } = await useApi(
      `/api/v1/save-cart/${cart.value?.id}`,
      {
        method: "POST",
        authToken: userToken.value,
        body: {
          name,
        },
      },
      { catchError: true },
    );

    if (err) {
      addToast("Failed to save cart.", "error");
    } else {
      addToast("Cart saved successfully.", "success");
      cart.value = null;
      cartCookie.value = null;
    }
  };

  const claimCart = async (id: string, user_token?: string) => {
    const { res } = await useApi<CartResponse>("/api/v1/claim-cart", {
      authToken: user_token ?? userToken.value,
      method: "POST",
      body: {
        cart_id: id,
      },
    });

    if (res?.data) {
      cart.value = res.data;
      cartCookie.value = res.data.id;
    }
  };

  const mergeCart = async (anon_cart_id: string, user_cart_id: string) => {
    const { res } = await useApi<CartResponse>("/api/v1/merge-carts", {
      authToken: userToken.value,
      method: "POST",
      body: {
        active_cart_id: user_cart_id,
        claim_cart_id: anon_cart_id,
      },
    });

    if (res?.data) {
      cart.value = res.data;
      cartCookie.value = res.data.id;
    }
  };

  const activateCart = async (id: string) => {
    const { res, err } = await useApi<CartResponse>(
      "/api/v1/activate-saved-cart",
      {
        method: "POST",
        authToken: userToken.value,
        body: {
          cart_id: id,
        },
      },
      {
        catchError: true,
      },
    );

    if (err) {
      addToast("Failed to activate saved cart.", "error");
    } else {
      cart.value = res?.data ?? null;
      cartCookie.value = id;
      savedCarts.value = null;
      fetchSavedCarts();
      router.push(paths.cart);
    }
  };

  const fetchSavedCarts = async () => {
    if (userToken.value) {
      const { res } = await useApi<BaseResponse<Cart[]>>(
        `/api/v1/saved-carts`,
        {
          authToken: userToken.value,
        },
      );

      savedCarts.value = res?.data ?? null;
    }
  };

  const shareSavedCart = async (id: string) => {
    const { err } = await useApi<BaseResponse<Cart[]>>(
      `/api/v1/share-cart/${id}`,
      {
        method: "POST",
        authToken: userToken.value,
      },
      {
        catchError: true,
      },
    );

    if (err) {
      addToast("Failed to share cart.", "error");
    } else {
      addToast("Cart shared successfully.", "success");
      cart.value = null;
      cartCookie.value = null;
    }

    fetchSavedCarts();
  };

  const deleteSavedCart = async (id: string) => {
    await useApi<BaseResponse<Cart[]>>(`/api/v1/my-cart/${id}`, {
      method: "DELETE",
      authToken: userToken.value,
    });
  };

  const resetCart = () => {
    cartCookie.value = null;
    cart.value = null;
    savedCarts.value = null;
  };

  return {
    cart,
    cartCookie,
    savedCarts,
    isCartEmpty,
    loadingProductId,
    resetCart,
    saveCart,
    initCart,
    isInCart,
    addToCart,
    claimCart,
    mergeCart,
    activateCart,
    quickAddToCart,
    updateQuantity,
    getItemQuantity,
    fetchSavedCarts,
    shareSavedCart,
    deleteSavedCart,
  };
}
